import { useEffect, useState } from "react";
import { Users, Loader2, AlertCircle } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ApiError, users as usersApi, type UserRow } from "@/lib/api";
import { ROLE_LABELS } from "@/types/rbac";
import type { Role } from "@/types/rbac";

export default function AdminUsers() {
  const [rows, setRows] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setRows(await usersApi.list());
      } catch (e) {
        setError(e instanceof ApiError ? e.message : "Failed to load users");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Users className="h-6 w-6" /> Users
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Members of your organisation. New users are added automatically when they sign in via Microsoft Entra (provided they're in the right security group).
        </p>
      </div>

      {error && (
        <Card className="mb-4 border-destructive bg-destructive/10">
          <CardContent className="p-3 flex items-center gap-2 text-sm">
            <AlertCircle className="h-4 w-4 text-destructive" /> {error}
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center text-muted-foreground flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading users...
          </CardContent>
        </Card>
      )}

      {!loading && rows.length === 0 && (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center text-muted-foreground">
            No users yet. Members will appear here as they sign in for the first time.
          </CardContent>
        </Card>
      )}

      {!loading && rows.length > 0 && (
        <Card className="bg-card border-border">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-3 px-4 font-medium">Name</th>
                  <th className="py-3 px-4 font-medium">Email</th>
                  <th className="py-3 px-4 font-medium">Role</th>
                  <th className="py-3 px-4 font-medium hidden sm:table-cell">First seen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id} className="border-b border-border/50 last:border-0">
                    <td className="py-3 px-4 font-medium text-foreground">
                      {u.full_name ?? "—"}
                    </td>
                    <td className="py-3 px-4 text-muted-foreground break-all">{u.email}</td>
                    <td className="py-3 px-4">
                      <Badge variant="secondary" className="text-xs whitespace-nowrap">
                        {ROLE_LABELS[u.role as Role] ?? u.role}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground text-xs hidden sm:table-cell whitespace-nowrap">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
